<template>
  <div
    class="ai-visibility-tracker-selector-item"
    @click="$emit('select', tracker)"
  >
    <status-dot
      :status="tracker.statusTone || tracker.status || 'active'"
      :size="8"
    />

    <div class="ai-visibility-tracker-selector-item__content">
      <input
        v-if="editing"
        ref="renameInput"
        v-model="draftTitle"
        class="ai-visibility-tracker-selector-item__input"
        type="text"
        aria-label="Report name"
        :disabled="renaming"
        @click.stop
        @mousedown.stop
        @keydown.enter.prevent="finishRename"
        @keydown.esc.prevent="cancelRename"
        @blur="finishRename"
      >
      <div
        v-else
        class="ai-visibility-tracker-selector-item__label"
        :title="label"
      >
        {{ label }}
      </div>

      <div class="ai-visibility-tracker-selector-item__meta">
        {{ metadata }}
      </div>
    </div>

    <v-menu
      v-model="menuOpen"
      offset-y
      left
      :close-on-content-click="true"
      content-class="ai-visibility-tracker-selector-menu"
    >
      <template #activator="{ on, attrs }">
        <button
          v-bind="attrs"
          v-on="on"
          type="button"
          class="ai-visibility-tracker-selector-item__actions"
          aria-label="Report actions"
          @click.stop
          @mousedown.stop
        >
          <i class="material-icons">more_horiz</i>
        </button>
      </template>

      <div class="ai-visibility-tracker-selector-menu__panel" @click.stop>
        <button
          type="button"
          class="ai-visibility-tracker-selector-menu__item"
          @click.stop="handleRenameClick"
        >
          <span class="material-icons-outlined">edit</span>
          <span>Rename report</span>
        </button>
        <button
          v-if="canChangeFrequency"
          type="button"
          class="ai-visibility-tracker-selector-menu__item"
          @click.stop="handleChangeFrequencyClick"
        >
          <span class="material-icons-outlined">sync</span>
          <span>Change Frequency</span>
        </button>
        <button
          v-if="canPauseOrResume"
          type="button"
          class="ai-visibility-tracker-selector-menu__item"
          :disabled="transitioning"
          @click.stop="handlePauseResumeClick"
        >
          <span class="material-icons-outlined">{{ pauseResumeIcon }}</span>
          <span>{{ pauseResumeLabel }}</span>
        </button>
        <button
          type="button"
          class="ai-visibility-tracker-selector-menu__item ai-visibility-tracker-selector-menu__item--danger"
          @click.stop="handleDeleteClick"
        >
          <span class="material-icons-outlined">delete</span>
          <span>Delete Report</span>
        </button>
      </div>
    </v-menu>
  </div>
</template>

<script>
import StatusDot from '../ui/display/StatusDot.vue'

function capitalize(value) {
  const text = String(value || '').trim()
  if (!text) {
    return 'Once'
  }
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`
}

export default {
  name: 'TrackerSelectorItem',
  components: {
    StatusDot,
  },
  props: {
    tracker: {
      type: Object,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    renaming: {
      type: Boolean,
      default: false,
    },
    transitioning: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editing: false,
      draftTitle: '',
      menuOpen: false,
    }
  },
  computed: {
    metadata() {
      return [
        `Report ID: ${this.tracker.id}`,
        this.frequencyLabel,
        this.questionCountLabel,
      ].join(' · ')
    },
    frequencyLabel() {
      if (this.tracker.status === 'inactive') {
        return 'Paused'
      }
      return capitalize(this.tracker.refreshFrequency)
    },
    questionCountLabel() {
      const count = Number(this.tracker.questionCount || 0)
      return `${count} ${count === 1 ? 'question' : 'questions'}`
    },
    canChangeFrequency() {
      return this.tracker.status === 'active' &&
        ['weekly', 'monthly', 'once'].includes(this.tracker.refreshFrequency)
    },
    isRecurringTracker() {
      return ['weekly', 'monthly'].includes(this.tracker.refreshFrequency)
    },
    canPauseOrResume() {
      return this.isRecurringTracker &&
        ['active', 'inactive'].includes(this.tracker.status)
    },
    isPausedTracker() {
      return this.tracker.status === 'inactive'
    },
    pauseResumeIcon() {
      return this.isPausedTracker ? 'play_arrow' : 'pause'
    },
    pauseResumeLabel() {
      return this.isPausedTracker ? 'Resume schedule' : 'Pause schedule'
    },
  },
  watch: {
    renaming(value) {
      if (!value && this.editing) {
        this.editing = false
      }
    },
  },
  methods: {
    handleRenameClick() {
      this.menuOpen = false
      this.startRename()
    },
    handleDeleteClick() {
      this.menuOpen = false
      this.$emit('delete', this.tracker)
    },
    handleChangeFrequencyClick() {
      this.menuOpen = false
      this.$emit('change-frequency', this.tracker)
    },
    handlePauseResumeClick() {
      if (this.transitioning) {
        return
      }
      this.menuOpen = false
      this.$emit(this.isPausedTracker ? 'resume' : 'pause', this.tracker)
    },
    startRename() {
      this.draftTitle = this.label
      this.editing = true
      this.$nextTick(() => {
        const input = this.$refs.renameInput
        if (input) {
          input.focus()
          input.select()
        }
      })
    },
    cancelRename() {
      this.editing = false
      this.draftTitle = this.label
    },
    finishRename() {
      if (!this.editing || this.renaming) {
        return
      }

      const title = this.draftTitle.trim()
      if (!title || title === this.label) {
        this.cancelRename()
        return
      }

      this.$emit('rename', {
        tracker: this.tracker,
        title,
        done: () => {
          this.editing = false
        },
        cancel: this.cancelRename,
      })
    },
  },
}
</script>

<style scoped lang="scss">

.ai-visibility-tracker-selector-item {
  position: relative;
  display: flex;
  width: 100%;
  min-width: 260px;
  align-items: center;
  gap: 10px;
  color: var(--v-mainColor-base);
  cursor: pointer;
  padding: 9px 6px;
}

.ai-visibility-tracker-selector-item__content {
  min-width: 0;
  flex: 1;
}

.ai-visibility-tracker-selector-item__label {
  overflow: hidden;
  color: var(--v-mainColor-base);
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-visibility-tracker-selector-item__meta {
  overflow: hidden;
  color: var(--v-darkGrayColor-base);
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-visibility-tracker-selector-item__input {
  width: 100%;
  min-height: 26px;
  border: 1px solid var(--v-gray13Color-base);
  border-radius: 6px;
  color: var(--v-mainColor-base);
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  padding: 3px 7px;
}

.ai-visibility-tracker-selector-item__input:focus {
  border-color: var(--v-mainColor-base);
  outline: none;
}

.ai-visibility-tracker-selector-item__actions {
  display: inline-flex;
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--v-darkGrayColor-base);
  cursor: pointer;
  padding: 0;
}

.ai-visibility-tracker-selector-item__actions:hover,
.ai-visibility-tracker-selector-item__actions:focus {
  background: var(--v-gray7Color-base);
  color: var(--v-mainColor-base);
  outline: none;
}

.ai-visibility-tracker-selector-item__actions i {
  font-size: 19px;
}

.ai-visibility-tracker-selector-menu__panel {
  min-width: 168px;
  border: 1px solid var(--v-gray13Color-base);
  border-radius: 8px;
  background: var(--v-cardBg-base);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
  padding: 6px;
}

.ai-visibility-tracker-selector-menu__item {
  display: flex;
  width: 100%;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--v-mainColor-base);
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 10px;
  text-align: left;
  align-items: center;
  gap: 4px;
}

.ai-visibility-tracker-selector-menu__item:hover,
.ai-visibility-tracker-selector-menu__item:focus {
  background: var(--v-gray7Color-base);
  outline: none;
}

.ai-visibility-tracker-selector-menu__item--danger {
  color: var(--v-redColor-base);
}

</style>
